/**
 * Enhanced Scenario Context Provider
 * Provides comprehensive scenario state management for the VRP system
 * 
 * Features:
 * - CRUD operations for scenarios
 * - Hierarchical project -> scenario relationships
 * - Automatic state refresh and caching
 * - Error handling with user-friendly messages
 * - Loading states for all async operations
 * - Integration with AuthContext and ProjectContext
 * - Optimistic updates with rollback support
 * - Auto-refresh on window focus
 */

import React, { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback, 
  useMemo,
  ReactNode 
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from './AuthContext';
import { useProject } from './ProjectContext';
import { useContextCoordinator } from '../hooks/useContextCoordinator';
import type { 
  Scenario, 
  ScenarioCreate, 
  ScenarioUpdate, 
  ScenarioWithDetails,
  Project
} from '../types/vrp';

// =============================================
// CONTEXT STATE INTERFACES
// =============================================

interface ScenarioContextState {
  // Current State
  currentProject: Project | null;
  currentScenario: Scenario | null;
  scenarios: Scenario[];
  
  // Loading States
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // Error States
  error: string | null;
  validationErrors: Record<string, string[]> | null;
}

interface ScenarioContextActions {
  // Scenario Selection
  setScenario: (scenario: Scenario | null) => void;
  
  // CRUD Operations
  createScenario: (data: ScenarioCreate) => Promise<Scenario>;
  updateScenario: (id: number, data: ScenarioUpdate) => Promise<Scenario>;
  deleteScenario: (id: number) => Promise<void>;
  duplicateScenario: (id: number, newName: string) => Promise<Scenario>;
  
  // Data Management
  refreshScenarios: () => Promise<void>;
  getScenarioDetails: (id: number) => Promise<ScenarioWithDetails | null>;
  
  // Utility Functions
  clearError: () => void;
  isScenarioSelected: (scenarioId: number) => boolean;
}

type ScenarioContextType = ScenarioContextState & ScenarioContextActions;

const ScenarioContext = createContext<ScenarioContextType | undefined>(undefined);

interface ScenarioProviderProps {
  children: ReactNode;
}

export const ScenarioProvider: React.FC<ScenarioProviderProps> = ({ children }) => {
  // =============================================
  // HOOKS AND DEPENDENCIES
  // =============================================
  
  const { user } = useAuth();
  const { currentProject } = useProject();
  const queryClient = useQueryClient();
  
  // Context Update Coordination System - Prevent cascade conflicts
  const { coordinateUpdate, isContextUpdating } = useContextCoordinator();
  
  // =============================================
  // LOCAL STATE
  // =============================================
  
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

  // =============================================
  // REACT QUERY SETUP
  // =============================================
  
  // Query for scenarios
  const {
    data: scenariosData = [],
    isLoading: isLoadingScenarios,
    error: scenariosError,
    refetch: refetchScenarios
  } = useQuery({
    queryKey: ['scenarios', currentProject?.id],
    queryFn: async () => {
      if (!currentProject?.id) return [];
      if (import.meta.env.DEV) {
        console.log('🎬 [ScenarioContext] Fetching scenarios for project:', currentProject.id);
      }
      const response = await apiClient.getScenarios(currentProject.id);
      if (import.meta.env.DEV) {
        console.log('🎬 [ScenarioContext] Raw API response:', response);
        console.log('🎬 [ScenarioContext] Response.data:', response?.data);
        console.log('🎬 [ScenarioContext] Response.data type:', typeof response?.data);
        console.log('🎬 [ScenarioContext] Response.data isArray:', Array.isArray(response?.data));
      }
      // Extract actual scenarios data from Edge Function response structure
      const scenarios = response?.data || response || [];
      if (import.meta.env.DEV) {
        console.log('🎬 [ScenarioContext] Final scenarios array:', scenarios);
      }
      return scenarios;
    },
    enabled: !!currentProject?.id && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: true,
    onError: (error: any) => {
      console.error('❌ [ScenarioContext] Failed to fetch scenarios:', error);
      // Check if this is a 404 error indicating Edge Functions not deployed
      if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        setError('Edge Functions not deployed. Please deploy Supabase Edge Functions.');
      } else {
        setError(error.message || 'Failed to fetch scenarios');
      }
    }
  });

  // =============================================
  // MUTATIONS
  // =============================================
  
  // Create scenario mutation
  const createScenarioMutation = useMutation({
    mutationFn: async (data: ScenarioCreate): Promise<Scenario> => {
      console.log('🎬 [ScenarioContext] Creating scenario:', data);
      const response = await apiClient.createScenario(data);
      // Extract actual scenario data from Edge Function response structure
      return response.data || response;
    },
    onSuccess: (newScenario: Scenario) => {
      console.log('✅ [ScenarioContext] Scenario created successfully:', newScenario?.name || 'Unnamed scenario');
      console.log('🔍 [ScenarioContext] Full scenario object:', newScenario);
      
      // Update query cache optimistically
      queryClient.setQueryData(
        ['scenarios', currentProject?.id],
        (oldData: Scenario[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return [...safeOldData, newScenario];
        }
      );
      
      // Auto-select the new scenario
      setCurrentScenario(newScenario);
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [ScenarioContext] Failed to create scenario:', error);
      
      if (error.message?.includes('validation')) {
        setValidationErrors(error.details?.field_errors || {});
      } else {
        setError(error.message || 'Failed to create scenario');
      }
    }
  });

  // Update scenario mutation
  const updateScenarioMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ScenarioUpdate }): Promise<Scenario> => {
      if (!currentProject?.id) throw new Error('No project selected');
      console.log('🎬 [ScenarioContext] Updating scenario:', id, data);
      const response = await apiClient.updateScenario(id, currentProject.id, data);
      // Extract actual scenario data from Edge Function response structure
      return response.data || response;
    },
    onSuccess: (updatedScenario: Scenario) => {
      console.log('✅ [ScenarioContext] Scenario updated successfully:', updatedScenario.name);
      
      // Update query cache optimistically
      queryClient.setQueryData(
        ['scenarios', currentProject?.id],
        (oldData: Scenario[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return safeOldData.map(scenario => 
            scenario.id === updatedScenario.id ? updatedScenario : scenario
          );
        }
      );
      
      // Update current scenario if it's the one being updated
      if (currentScenario?.id === updatedScenario.id) {
        setCurrentScenario(updatedScenario);
      }
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [ScenarioContext] Failed to update scenario:', error);
      
      if (error.message?.includes('validation')) {
        setValidationErrors(error.details?.field_errors || {});
      } else {
        setError(error.message || 'Failed to update scenario');
      }
    }
  });

  // Delete scenario mutation
  const deleteScenarioMutation = useMutation({
    mutationFn: async (scenarioId: number): Promise<void> => {
      if (!currentProject?.id) throw new Error('No project selected');
      console.log('🎬 [ScenarioContext] Deleting scenario:', scenarioId);
      await apiClient.deleteScenario(scenarioId, currentProject.id);
    },
    onSuccess: (_, deletedScenarioId: number) => {
      console.log('✅ [ScenarioContext] Scenario deleted successfully:', deletedScenarioId);
      
      // Update query cache optimistically
      queryClient.setQueryData(
        ['scenarios', currentProject?.id],
        (oldData: Scenario[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return safeOldData.filter(scenario => scenario.id !== deletedScenarioId);
        }
      );
      
      // Clear current scenario if it was deleted
      if (currentScenario?.id === deletedScenarioId) {
        setCurrentScenario(null);
      }
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [ScenarioContext] Failed to delete scenario:', error);
      setError(error.message || 'Failed to delete scenario');
    }
  });

  // =============================================
  // ACTION FUNCTIONS - COORDINATED
  // =============================================
  
  const setScenario = useCallback((scenario: Scenario | null) => {
    // Skip if context is already updating to prevent cascade
    if (isContextUpdating('ScenarioContext') || isAutoSelecting) {
      console.log('🔒 [ScenarioContext] Skipping scenario update - context already updating or auto-selecting');
      return;
    }

    coordinateUpdate('ScenarioContext', async () => {
      if (import.meta.env.DEV) {
        console.log('🎬 [ScenarioContext] Setting current scenario:', scenario?.name || 'none');
      }
      setCurrentScenario(scenario);
      
      // Coordinate localStorage writes to prevent conflicts
      await coordinateUpdate('LocalStorage', async () => {
        if (scenario && currentProject?.id) {
          localStorage.setItem(
            `vrp-current-scenario-${currentProject.id}`,
            JSON.stringify({
              id: scenario.id,
              name: scenario.name,
              project_id: scenario.project_id
            })
          );
        } else if (currentProject?.id) {
          localStorage.removeItem(`vrp-current-scenario-${currentProject.id}`);
        }
      }, 10); // Low priority for localStorage operations
    }, 2); // Medium priority for scenario updates
  }, [currentProject?.id, coordinateUpdate, isContextUpdating, isAutoSelecting]);
  
  const createScenario = useCallback(async (data: ScenarioCreate): Promise<Scenario> => {
    if (!currentProject?.id) {
      throw new Error('No project selected');
    }
    
    const scenarioData = {
      ...data,
      project_id: currentProject.id
    };
    
    return createScenarioMutation.mutateAsync(scenarioData);
  }, [currentProject?.id, createScenarioMutation]);
  
  const updateScenario = useCallback(async (id: number, data: ScenarioUpdate): Promise<Scenario> => {
    return updateScenarioMutation.mutateAsync({ id, data });
  }, [updateScenarioMutation]);
  
  const deleteScenario = useCallback(async (id: number): Promise<void> => {
    await deleteScenarioMutation.mutateAsync(id);
  }, [deleteScenarioMutation]);
  
  const duplicateScenario = useCallback(async (id: number, newName: string): Promise<Scenario> => {
    const scenariosList = Array.isArray(scenariosData) ? scenariosData : [];
    const originalScenario = scenariosList.find(s => s.id === id);
    if (!originalScenario) {
      throw new Error('Scenario not found');
    }
    
    const duplicateData: ScenarioCreate = {
      name: newName,
      description: `Copy of ${originalScenario.description || originalScenario.name}`,
      project_id: originalScenario.project_id,
      start_date: originalScenario.start_date,
      planning_horizon_days: originalScenario.planning_horizon_days,
      optimization_objective: originalScenario.optimization_objective,
      parent_scenario_id: originalScenario.id
    };
    
    return createScenario(duplicateData);
  }, [scenariosData, createScenario]);
  
  const refreshScenarios = useCallback(async (): Promise<void> => {
    console.log('🎬 [ScenarioContext] Manually refreshing scenarios');
    await refetchScenarios();
  }, [refetchScenarios]);
  
  const getScenarioDetails = useCallback(async (id: number): Promise<ScenarioWithDetails | null> => {
    if (!currentProject?.id) return null;
    
    try {
      console.log('🎬 [ScenarioContext] Fetching scenario details:', id);
      const scenario = await apiClient.getScenario(id, currentProject.id);
      return scenario as ScenarioWithDetails;
    } catch (error: any) {
      console.error('❌ [ScenarioContext] Failed to get scenario details:', error);
      setError(error.message || 'Failed to get scenario details');
      return null;
    }
  }, [currentProject?.id]);
  
  const clearError = useCallback(() => {
    setError(null);
    setValidationErrors(null);
  }, []);
  
  const isScenarioSelected = useCallback((scenarioId: number): boolean => {
    return currentScenario?.id === scenarioId;
  }, [currentScenario?.id]);
  
  // =============================================
  // COMPUTED VALUES
  // =============================================
  
  // Ensure scenarios is always an array with better defensive handling
  const scenarios = useMemo(() => {
    if (Array.isArray(scenariosData)) {
      return scenariosData;
    } else if (scenariosData?.data && Array.isArray(scenariosData.data)) {
      return scenariosData.data;
    }
    return [];
  }, [scenariosData]);

  // =============================================
  // AUTO-SELECTION LOGIC - COORDINATED
  // =============================================
  
  // =============================================
  // CASCADE PREVENTION LOGIC
  // =============================================

  // Auto-select scenario when project changes or scenarios load (FIXED to prevent cascade loops)
  useEffect(() => {
    if (!currentProject?.id || scenarios.length === 0 || isAutoSelecting || isContextUpdating('ScenarioContext')) {
      if (!currentProject?.id || scenarios.length === 0) {
        setCurrentScenario(null);
      }
      return; // Prevent cascade during auto-selection
    }
    
    setIsAutoSelecting(true);
    
    // Debounced auto-selection logic
    const timeoutId = setTimeout(() => {
      coordinateUpdate('ScenarioContext', async () => {
        try {
          // Try to restore from localStorage
          const savedScenarioKey = `vrp-current-scenario-${currentProject.id}`;
          const savedScenarioData = localStorage.getItem(savedScenarioKey);
          
          if (savedScenarioData) {
            try {
              const savedScenario = JSON.parse(savedScenarioData);
              
              // Validate the parsed data structure
              if (!savedScenario || typeof savedScenario !== 'object' || 
                  savedScenario.id == null || isNaN(Number(savedScenario.id))) {
                console.warn('⚠️ [ScenarioContext] Invalid saved scenario data structure:', savedScenario);
                localStorage.removeItem(savedScenarioKey);
              } else {
                const scenarioId = Number(savedScenario.id);
                const foundScenario = scenarios.find(s => s.id === scenarioId);
                
                if (foundScenario) {
                  if (import.meta.env.DEV) {
                    console.log('🎬 [ScenarioContext] Restoring saved scenario:', foundScenario.name, 'ID:', foundScenario.id);
                  }
                  setCurrentScenario(foundScenario);
                  return;
                } else {
                  if (import.meta.env.DEV) {
                    console.log('⚠️ [ScenarioContext] Saved scenario not found in current list, clearing localStorage');
                  }
                  localStorage.removeItem(savedScenarioKey);
                }
              }
            } catch (error) {
              if (import.meta.env.DEV) {
                console.warn('⚠️ [ScenarioContext] Failed to parse saved scenario data:', error);
              }
              localStorage.removeItem(savedScenarioKey);
            }
          }
          
          // If no saved scenario or it's not available, auto-select first active scenario
          const activeScenarios = scenarios.filter(s => s.status === 'active');
          if (activeScenarios.length > 0) {
            if (import.meta.env.DEV) {
              console.log('🎬 [ScenarioContext] Auto-selecting first active scenario:', activeScenarios[0]?.name || 'Unnamed scenario');
            }
            setCurrentScenario(activeScenarios[0]);
          } else if (scenarios.length > 0) {
            if (import.meta.env.DEV) {
              console.log('🎬 [ScenarioContext] Auto-selecting first scenario:', scenarios[0]?.name || 'Unnamed scenario');
            }
            setCurrentScenario(scenarios[0]);
          }
        } finally {
          setIsAutoSelecting(false);
        }
      }, 3); // Lower priority for auto-selection
    }, 200); // Debounce auto-selection to prevent rapid triggering
    
    return () => {
      clearTimeout(timeoutId);
      setIsAutoSelecting(false);
    };
  }, [currentProject?.id]); // Remove scenarios from dependencies to prevent cascade

  // Clear current scenario when project changes
  useEffect(() => {
    if (!currentProject?.id) {
      setCurrentScenario(null);
      setError(null);
      setValidationErrors(null);
    }
  }, [currentProject?.id]);
  
  // =============================================
  // ERROR HANDLING
  // =============================================
  
  // Handle query errors
  useEffect(() => {
    if (scenariosError) {
      const errorMessage = scenariosError instanceof Error ? scenariosError.message : 'Failed to fetch scenarios';
      console.error('❌ [ScenarioContext] Query error:', errorMessage);
      setError(errorMessage);
    }
  }, [scenariosError]);

  // =============================================
  // COMPUTED STATE
  // =============================================
  
  // Memoize loading states to prevent unnecessary re-renders
  const loadingStates = useMemo(() => ({
    isLoading: isLoadingScenarios,
    isCreating: createScenarioMutation.isPending,
    isUpdating: updateScenarioMutation.isPending,
    isDeleting: deleteScenarioMutation.isPending,
  }), [
    isLoadingScenarios,
    createScenarioMutation.isPending,
    updateScenarioMutation.isPending,
    deleteScenarioMutation.isPending,
  ]);

  // Memoize error states to prevent unnecessary re-renders
  const errorStates = useMemo(() => ({
    error,
    validationErrors,
  }), [error, validationErrors]);

  // Memoize action functions to prevent unnecessary re-renders
  const actionFunctions = useMemo(() => ({
    setScenario,
    createScenario,
    updateScenario,
    deleteScenario,
    duplicateScenario,
    refreshScenarios,
    getScenarioDetails,
    clearError,
    isScenarioSelected,
  }), [
    setScenario,
    createScenario,
    updateScenario,
    deleteScenario,
    duplicateScenario,
    refreshScenarios,
    getScenarioDetails,
    clearError,
    isScenarioSelected,
  ]);

  const contextValue = useMemo<ScenarioContextType>(() => ({
    // State
    currentProject,
    currentScenario,
    scenarios,
    
    // Loading States
    ...loadingStates,
    
    // Error States
    ...errorStates,
    
    // Actions
    ...actionFunctions,
  }), [
    currentProject,
    currentScenario,
    scenarios,
    loadingStates,
    errorStates,
    actionFunctions,
  ]);
  
  // =============================================
  // DEBUG LOGGING
  // =============================================
  
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🎬 [ScenarioContext] State update:', {
        currentProject: currentProject?.name || 'none',
        currentScenario: currentScenario?.name || 'none',
        scenariosCount: scenarios.length,
        isLoading: isLoadingScenarios,
        error: error || 'none'
      });
    }
  }, [currentProject, currentScenario, scenarios, isLoadingScenarios, error]);

  // =============================================
  // RENDER
  // =============================================

  return (
    <ScenarioContext.Provider value={contextValue}>
      {children}
    </ScenarioContext.Provider>
  );
};

// =============================================
// CUSTOM HOOK
// =============================================

export const useScenario = (): ScenarioContextType => {
  const context = useContext(ScenarioContext);
  
  if (context === undefined) {
    throw new Error('useScenario must be used within a ScenarioProvider');
  }
  
  return context;
};

// =============================================
// EXPORT DEFAULT
// =============================================

export default ScenarioProvider;